import Vue from 'vue';
import {
  createExportTask,
  createRTInboundExportTask,
} from '@/api/wms-config/download-center';
import { ExportModule, ExportTaskType } from '@/ConstEnums';
import { EventBus, i18n } from '@/i18n';
import { errorReport } from '@/utils/tools';
import { EXPORT_POPOVER_EVENT } from '@/common/constant';
import store from '@/store';

export interface ExportButtonProps {
  exportModule: ExportModule;
  exportTaskType: ExportTaskType;
  exportExtraData?: Record<string, any>;
  getExportExtraData?: () => Record<string, any>;
  beforeExport?: (params: Record<string, any>) => boolean | Promise<boolean>;
  showError?: boolean | undefined; // 是否显示错误信息
}

type Listener = (loading: boolean) => void;

export class ExportButtonCore {
  private loading: boolean;
  private listeners: Listener[] = [];
  private exportModule: ExportButtonProps['exportModule'];
  private exportTaskType: ExportButtonProps['exportTaskType'];
  private getExportExtraData: ExportButtonProps['getExportExtraData'];
  private showError: boolean;

  constructor(options: ExportButtonProps) {
    this.exportModule = options.exportModule;
    this.exportTaskType = options.exportTaskType;
    this.getExportExtraData = options.getExportExtraData;
    this.showError = options.showError ?? false;
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((fn) => fn !== listener);
    };
  }

  public getLoading(): boolean {
    return this.loading;
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.loading));
  }

  public async handleExport(
    extraData: ExportButtonProps['exportExtraData'],
    beforeExport?: ExportButtonProps['beforeExport']
  ): Promise<void> {
    try {
      if (this.loading) return;

      const exportExtraData = {
        ...extraData,
        ...this.getExportExtraData?.(),
      };

      // 预检不通过，不导出
      if (typeof beforeExport === 'function') {
        const result = await beforeExport(exportExtraData);
        if (!result) return;
      }

      this.loading = true;
      this.notifyListeners();

      let res: any;
      const params = {
        export_module: this.exportModule,
        task_type: this.exportTaskType,
        extra_data: JSON.stringify(exportExtraData),
      };
      // 退货入库导出使用新接口
      if (
        this.exportModule === ExportModule.RETURN__INBOUND &&
        store?.state?.warehouseDetail?.warehouse?.is_rms
      ) {
        res = await createRTInboundExportTask(params);
      } else {
        res = await createExportTask(params);
      }

      if (res.retcode === 0) {
        Vue.prototype.$message({
          type: 'success',
          message: i18n('Create Export Success!'),
        });
        EventBus.$emit(EXPORT_POPOVER_EVENT);
      } else if (this.showError) {
        Vue.prototype.$message({
          type: 'error',
          message: i18n(res.message),
        });
      }
    } catch (error) {
      errorReport(error);
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  }
}
